import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { useAuth, formatApiErrorDetail } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export const AdminSettingsPage = () => {
  const { checkAuth } = useAuth();
  const [settings, setSettings] = useState({ email: '', username: '', name: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    adminAPI.getSettings()
      .then(res => setSettings(res.data))
      .catch(() => toast.error('Failed to load'));
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await adminAPI.updateSettings({
        email: settings.email,
        username: settings.username,
        name: settings.name
      });
      toast.success('Admin profile updated');
      checkAuth();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Update failed');
    } finally {
      setProfileLoading(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await adminAPI.updateSettings({
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Update failed');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-2xl" data-testid="admin-settings-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Settings</h1>
          <p className="mt-2 text-muted-foreground">Manage your administrator account</p>
        </div>

        <Card>
          <CardHeader><CardTitle>Account Information</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({...settings, email: e.target.value})}
                  className="h-11"
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  value={settings.username || ''}
                  onChange={(e) => setSettings({...settings, username: e.target.value})}
                  className="h-11"
                  data-testid="admin-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={settings.name || ''}
                  onChange={(e) => setSettings({...settings, name: e.target.value})}
                  className="h-11"
                />
              </div>
              <Button type="submit" disabled={profileLoading} data-testid="save-admin-profile">
                {profileLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Change Password</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={savePassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Current Password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="h-11"
                  required
                  data-testid="admin-current-password"
                />
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-11"
                  required
                  data-testid="admin-new-password"
                />
              </div>
              <div className="space-y-2">
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-11"
                  required
                />
              </div>
              <Button type="submit" disabled={passwordLoading} data-testid="save-admin-password">
                {passwordLoading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            <strong>Security note:</strong> After changing your credentials, make sure to log out and back in on all your devices.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
};
